import { MIN_CELL_WIDTH, MAX_CELL_WIDTH } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { InputSize } from "@/components/input/InputSize";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useGetPMNodeDomRect } from "@/components/tiptap/utils/useGetPMNodeRect";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeWithPos } from "@tiptap/core";

type Props = {
  doc: DocumentModel;
  tableCell: NodeWithPos;
  value?: number | null;
  onChange: (value: number | null) => void;
};

export const TableColumnWidth = observer(function TableColumnWidth({
  doc,
  tableCell,
  value,
  onChange,
}: Props) {
  const { tiptap } = doc.editor;
  const t = useTranslations("sidebar.tableCellStyle");
  const { width } = useGetPMNodeDomRect(tiptap?.view, tableCell.pos) ?? {};
  const columnWidth = width ? Math.round(width) : undefined;

  const items = [
    {
      title: t("customWidth"),
      icon: <Icon name="CustomWidth" />,
      active: !!value,
      onClick: () => {
        onChange(columnWidth ?? MIN_CELL_WIDTH);
      },
    },
    {
      title: t("autoWidth"),
      icon: <Icon name="AutoWidth" />,
      active: value === null,
      onClick: () => {
        onChange(null);
      },
    },
  ];

  return (
    <div className="relative">
      <InputSize
        value={value ?? ""}
        type="width"
        label={t("columnWidth")}
        min={MIN_CELL_WIDTH}
        max={MAX_CELL_WIDTH}
        incrementFrom={width ? columnWidth : undefined}
        placeholder={width ? `${columnWidth}` : undefined}
        inputClassName="pr-3"
        onChange={onChange}
      />
      <Dropdown
        anchor={{ to: "bottom", padding: "10px" }}
        className="absolute top-0 right-0 bottom-0 w-3"
        toggleButtonLabel={t("widthSettings")}
        toggleButtonComponent={<Icon name="ArrowDown" className="[&>path]:stroke-document-text" />}
        items={items}
      />
    </div>
  );
});
